import { TemplateForm } from "@/features/dashboard/domain/services/socialTemplateService";
import { KroButton } from "features/components/buttons/kroButton";
import { FormApi, ReactFormApi } from "@tanstack/react-form";

const SampleMessage = ({ form
  , handleShareToWhatsApp,
  handleCopyToClipboard,
  isLoading, copied,
  fundraisingOptions,
  title }:
  {
    form: FormApi<TemplateForm, undefined> & ReactFormApi<TemplateForm, undefined>,
    handleShareToWhatsApp: () => void,
    handleCopyToClipboard: () => void,
    isLoading: boolean, copied: boolean,
    fundraisingOptions: string[],
    title: string
  }) => {
  return <div className="space-y-4 lg:max-w-[872px]" onClick={(e) => {
    e.stopPropagation();
    e.preventDefault();
  }}>
    {/* Message Preview with Inline Editing */}
    <div className="rounded-[8px] bg-[#F8FAFB]">
      <div className="space-y-2 p-4 lg:p-5">
        <p>
          Dear
          <form.Field
            name="recipientName"
            children={(field) => (
              <input
                id={field.name}
                name={field.name}
                className="text-sm inline-block min-w-[60px] sm:min-w-[100px] max-w-[150px] border-none bg-[#F8FAFB] px-2 py-1 font-dm-sans font-medium text-gray-800 outline-none"
                placeholder="recipient name"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            )}
          />
        </p>

        <p className="mt-4">
          I only have
          <form.Field
            name="daysRemaining"
            children={(field) => (
              <input
                id={field.name}
                name={field.name}
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="text-sm inline-block min-w-[40px] sm:min-w-[60px] max-w-[80px] border-none bg-[#F8FAFB] px-2 py-1 font-dm-sans font-medium text-gray-800 outline-none"
                placeholder="number"
                style={{
                  width: `${Math.max(field.state.value.length, 1)}ch`,
                }}
              />
            )}
          />
          days to finish fundraising for{" "}
          <form.Field
            name="fundraisingPurpose"
            children={(field) => (
              <select
                id={field.name}
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="text-sm inline-block rounded-[28px] border border-[#4FBA6D] bg-white  py-1 font-dm-sans font-medium text-[#222530] focus:outline-none"
              >
                {fundraisingOptions.map((option) => (
                  <option
                    className="px-4 font-dm-sans text-[16px] font-normal leading-[100%] tracking-[-1.5%] text-[#222530]"
                    key={option}
                    value={option}
                  >
                    {option}
                  </option>
                ))}
              </select>
            )}
          />
        </p>

        <p className="mt-4">
          If only{" "}
          <form.Field
            name="numberOfPeople"
            children={(field) => (
              <input
                id={field.name}
                name={field.name}
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="text-sm inline-block min-w-[60px] sm:min-w-[100px] max-w-[200px] border-none bg-[#F8FAFB] px-2 py-1 font-dm-sans font-medium text-gray-800 outline-none"
                placeholder="number"
                style={{
                  width: `${Math.max(field.state.value.length, 1)}ch`,
                }}
              />
            )}
          />
          people donated{" "}
          <form.Field
            name="donationAmount"
            children={(field) => (
              <input
                id={field.name}
                name={field.name}
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="text-sm inline-block min-w-[110px] sm:min-w-[110px] max-w-[150px] border-none bg-[#F8FAFB] px-2 py-1 font-dm-sans font-medium text-gray-800 outline-none"
                placeholder="amount"
                style={{
                  width: `${Math.max(field.state.value.length, 1)}ch`,
                }}
              />
            )}
          />{" "}
          to my fundraiser, I would{" "}
          <form.Field
            name="fundraisingGoal"
            children={(field) => (
              <input
                id={field.name}
                name={field.name}
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="text-sm inline-block min-w-[300px] sm:min-w-[300px] max-w-[300px] border-none bg-[#F8FAFB] px-2 py-1 font-dm-sans font-medium text-gray-800 outline-none"
                placeholder="goal"
                style={{
                  width: `${Math.min(Math.max(field.state.value.length, 10), 25)}ch`,
                }}
              />
            )}
          />
          Would you be able to help make this a reality?
        </p>

        <p className="mt-4 font-dm-sans">
          To donate, just click the link to my fundraiser below:
        </p>

        <p className="mt-4">
          <form.Field
            name="fundraiserLink"
            children={(field) => (
              <input
                id={field.name}
                name={field.name}
                type="url"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="text-sm inline-block w-full min-w-[200px] border-none bg-[#F8FAFB] px-2 py-1 font-dm-sans font-medium text-blue-600 underline outline-none"
                placeholder="https://giv.com/your-fundraiser-link"
              />
            )}
          />
        </p>

        <p className="mt-4 font-dm-sans">
          It would also really help if you could share my fundraiser link
          with everyone you know. The more eyes on my fundraiser, the
          better!
        </p>

        <p className="mt-4 font-dm-sans">
          I appreciate all of your support! I wouldn't be able to
          accomplish
          <form.Field
            name="accomplishment"
            children={(field) => (
              <input
                id={field.name}
                name={field.name}
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="text-sm inline-block min-w-[60px] sm:min-w-[80px] max-w-[200px] border-none bg-[#F8FAFB] px-2 py-1 font-dm-sans font-medium text-gray-800 outline-none"
                placeholder="accomplishment"
                style={{
                  width: `${Math.min(Math.max(field.state.value.length, 10), 25)}ch`,
                }}
              />
            )}
          />
          without your help.
        </p>

        <p className="mt-4 font-dm-sans">
          <form.Field
            name="userName"
            children={(field) => (
              <input
                id={field.name}
                name={field.name}
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                className="text-sm inline-block min-w-[60px] sm:min-w-[100px] max-w-[150px] border-none bg-[#F8FAFB] px-2 py-1 font-dm-sans font-medium text-gray-800 outline-none"
                placeholder="your name"
              />
            )}
          />
        </p>
      </div>
      <div className="flex flex-col p-4 lg:p-5 gap-3 border-t border-t-[#E1E4EA] pt-4 sm:flex-row">
        <KroButton
          className="h-[36px] w-full sm:w-auto pl-4 pr-4"
          iconSize={16}
          title={title}
          onclick={handleShareToWhatsApp}
          isLoading={isLoading}
        />
        <div
          className="flex h-[36px] hover:scale-105 transition-all duration-300 w-full sm:w-[103px] cursor-pointer items-center justify-center rounded-[32px] border border-neutral-200 bg-neutral-50"
          onClick={handleCopyToClipboard}
        >
          <p className="font-inter text-[12px] font-medium leading-[18px] tracking-[-0.5%] text-neutral-900">
            {copied ? "Copied!" : "Copy"}
          </p>
        </div>
      </div>
    </div>

    {/* Action Buttons */}
  </div>;
};

export default SampleMessage;